import { useTranslation } from 'react-i18next';

import {
  ECLIPSE_TIMELINE_END,
  ECLIPSE_TIMELINE_START,
  type LocalEclipseDetails,
  type LocalEclipseMoment,
} from '../lib/eclipse';

interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  complete: boolean;
}

interface EclipseTimelineProps {
  displayTime: Date;
  countdown: CountdownParts;
  details: LocalEclipseDetails | null;
  moment: LocalEclipseMoment | null;
  onTimeChange: (date: Date) => void;
}

const durationSeconds = (ECLIPSE_TIMELINE_END.getTime() - ECLIPSE_TIMELINE_START.getTime()) / 1000;
const clamp = (value: number) => Math.max(0, Math.min(100, value));
const positionOf = (date: Date) =>
  clamp(
    ((date.getTime() - ECLIPSE_TIMELINE_START.getTime()) /
      (ECLIPSE_TIMELINE_END.getTime() - ECLIPSE_TIMELINE_START.getTime())) *
      100,
  );

export function EclipseTimeline({
  displayTime,
  countdown,
  details,
  moment,
  onTimeChange,
}: EclipseTimelineProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-GB';
  const formatTime = (date: Date, includeSeconds = false) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
      hour12: false,
    }).format(date);
  const elapsedSeconds = Math.round(
    (displayTime.getTime() - ECLIPSE_TIMELINE_START.getTime()) / 1000,
  );
  const contacts = details?.contacts ?? null;
  const partialStart = contacts ? positionOf(contacts.c1) : 0;
  const partialEnd = contacts ? positionOf(contacts.c4) : 0;
  const totalStart = contacts?.c2 ? positionOf(contacts.c2) : null;
  const totalEnd = contacts?.c3 ? positionOf(contacts.c3) : null;
  const selectedPosition = positionOf(displayTime);

  return (
    <section className="timeline-card" aria-label={t('time.preview')}>
      <div className="timeline-topline">
        <div className="countdown-compact">
          <span>{t('countdown.title')}</span>
          <strong>
            {countdown.complete
              ? t('countdown.complete')
              : `${countdown.days}${t('countdown.days')} ${countdown.hours}${t('countdown.hours')} ${countdown.minutes}${t('countdown.minutes')}`}
          </strong>
        </div>
        <div className="timeline-selected-time">
          <span>{t('time.eclipseForecast')}</span>
          <strong>{formatTime(displayTime, true)}</strong>
          <small>CEST</small>
        </div>
      </div>

      <div className="time-slider-row eclipse-slider-row">
        <span>{formatTime(ECLIPSE_TIMELINE_START)}</span>
        <input
          type="range"
          min="0"
          max={durationSeconds}
          step="10"
          value={elapsedSeconds}
          aria-label={t('time.preview')}
          aria-valuetext={formatTime(displayTime, true)}
          onChange={(event) =>
            onTimeChange(
              new Date(ECLIPSE_TIMELINE_START.getTime() + Number(event.target.value) * 1000),
            )
          }
        />
        <span>{formatTime(ECLIPSE_TIMELINE_END)}</span>
      </div>

      <div className={`local-phase-track ${contacts ? '' : 'is-empty'}`}>
        {contacts && (
          <>
            <span
              className="partial-window"
              style={{
                left: `${partialStart}%`,
                width: `${Math.max(0, partialEnd - partialStart)}%`,
              }}
            />
            {totalStart !== null && totalEnd !== null && (
              <span
                className="totality-window"
                style={{ left: `${totalStart}%`, width: `${Math.max(0, totalEnd - totalStart)}%` }}
              />
            )}
            <span className="maximum-tick" style={{ left: `${positionOf(contacts.maximum)}%` }} />
          </>
        )}
        <span className="timeline-cursor" style={{ left: `${selectedPosition}%` }} />
      </div>

      <div className="timeline-phase-readout" aria-live="polite">
        {moment && contacts ? (
          <>
            <span className={`timeline-phase-name phase-${moment.phase}`}>
              {t(`location.phase_${moment.phase}`)}
            </span>
            <strong
              aria-label={`${(moment.obscuration * 100).toFixed(1)}% ${t('location.obscuration')}`}
            >
              {(moment.obscuration * 100).toFixed(1)}%
            </strong>
          </>
        ) : (
          <span>{t('time.selectPoint')}</span>
        )}
      </div>
    </section>
  );
}
